export interface SeoConfig {
  title: string;
  description: string;
  canonical: string;
  image?: string;
  type?: "website" | "article";
  robots?: string;
  structuredData?: Record<string, unknown>[];
}

const origin = "https://kleanbg.com";
const defaultImage = `${origin}/assets/umbrella-portrait.webp`;

export const alternativeFaqs = [
  {
    question: "Is remove.bg shutting down?",
    answer: "remove.bg says its background-removal functionality is moving into Canva. Its API documentation separately says background removal moves to Leonardo.Ai on December 1, 2026. The accurate description is a transition, not that every remove.bg service disappeared at once.",
  },
  {
    question: "What is a good remove.bg alternative for a transparent PNG?",
    answer: "Choose a focused tool that preserves transparency, lets you inspect difficult edges, and exports useful files. KleanBG adds seller presets, small batch queues, and ZIP download without keeping an image archive.",
  },
  {
    question: "Can I use KleanBG without an account?",
    answer: "Yes. An anonymous browser gets one successful removal. A verified free account gets three successful removals per UTC day.",
  },
  {
    question: "Does KleanBG replace the remove.bg API?",
    answer: "No. KleanBG is currently a browser product, not a drop-in API replacement. Existing remove.bg API users should follow the official Leonardo.Ai migration documentation and test against their own workload.",
  },
  {
    question: "Does KleanBG store uploaded images?",
    answer: "KleanBG streams the image through Cloudflare for processing and returns the result to the browser. It does not intentionally retain originals, results, replacement backgrounds, or filenames.",
  },
] as const;

const pageSeo: Record<string, SeoConfig> = {
  "/": {
    title: "KleanBG - Fast, Private Background Remover",
    description: "Remove backgrounds from people, products, pets, and graphics. Download a transparent PNG or prepare marketplace images and batch ZIPs.",
    canonical: `${origin}/`,
    image: defaultImage,
    structuredData: [{
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "KleanBG",
      url: `${origin}/`,
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Any",
      description: "Fast, private background removal for people, products, pets, and graphics, with transparent PNG and marketplace exports.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    }],
  },
  "/remove-bg-alternative": {
    title: "remove.bg Alternative as It Moves to Canva | KleanBG",
    description: "What is changing at remove.bg, where its API is moving, and how to choose a fast remove.bg alternative for transparent PNG cutouts.",
    canonical: `${origin}/remove-bg-alternative`,
    image: `${origin}/assets/remove-bg-alternative-guide.webp`,
    type: "article",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "A practical remove.bg alternative as it moves to Canva",
        description: "An accurate guide to the remove.bg transition, browser alternatives, transparent PNG workflows, and the separate API migration.",
        image: [`${origin}/assets/remove-bg-alternative-guide.webp`],
        datePublished: "2026-09-03",
        dateModified: "2026-09-03",
        author: { "@type": "Person", name: "Koby Ofek", url: "https://x.com/kobyof" },
        publisher: { "@type": "Organization", name: "KleanBG", url: `${origin}/` },
        mainEntityOfPage: `${origin}/remove-bg-alternative`,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: alternativeFaqs.map(({ question, answer }) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text: answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "KleanBG", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "remove.bg alternative guide", item: `${origin}/remove-bg-alternative` },
        ],
      },
    ],
  },
  "/pricing": { title: "Limits and Free Usage | KleanBG", description: "KleanBG image, batch, and daily limits for anonymous visitors, verified free accounts, and Community access.", canonical: `${origin}/pricing`, image: defaultImage },
  "/privacy": { title: "Image Privacy and Retention | KleanBG", description: "KleanBG uploads images only for removal, does not keep them or use them for training, and strips EXIF and GPS during local export.", canonical: `${origin}/privacy`, image: defaultImage },
  "/terms": { title: "Terms | KleanBG", description: "The terms that apply when you use KleanBG for automatic background removal and browser-based image editing.", canonical: `${origin}/terms`, image: defaultImage },
  "/help": { title: "Background Removal Help | KleanBG", description: "Learn KleanBG formats, limits, clipboard and URL upload, transparent PNG export, marketplace presets, batch ZIPs, and edge refinement.", canonical: `${origin}/help`, image: defaultImage },
  "/contact": { title: "Contact | KleanBG", description: "Contact KleanBG for product help, account support, privacy requests, or abuse and copyright reports.", canonical: `${origin}/contact`, image: defaultImage },
  "/account": { title: "Account | KleanBG", description: "View your KleanBG removal allowance, usage total, and Community access request.", canonical: `${origin}/account`, image: defaultImage, robots: "noindex,follow" },
};

const fallbackSeo: SeoConfig = {
  title: "Page not found | KleanBG",
  description: "The requested KleanBG page could not be found.",
  canonical: `${origin}/`,
  image: defaultImage,
  robots: "noindex,follow",
};

export const knownPagePaths = new Set(Object.keys(pageSeo));

const prerenderContent: Partial<Record<string, string>> = {
  "/pricing": `<main class="prerender-shell"><h1>Simple KleanBG limits</h1><p>One anonymous image without an account. Verified free accounts receive three successful removals per UTC day and can queue up to 10 images. Community accounts receive 20 daily and can queue up to 50.</p><h2>Included exports</h2><p>Use seller presets, simple edge refinement, PNG, JPEG or WebP output, and batch ZIP download. Commercial use is allowed when you hold the necessary rights.</p></main>`,
  "/privacy": `<main class="prerender-shell"><h1>Privacy, in plain language</h1><p>Images upload to Cloudflare only for background removal. KleanBG does not intentionally store originals, cutouts, batch files, filenames, or image URLs, and does not use images for training.</p><h2>Local export</h2><p>Editing, resizing, format conversion, EXIF and GPS removal, and ZIP creation happen in your browser. Account quota metadata is stored separately from images.</p></main>`,
  "/terms": `<main class="prerender-shell"><h1>KleanBG terms of use</h1><p>Use KleanBG only for images you have the right to process. You retain your rights in your files and output. Commercial use is allowed when you have all necessary rights.</p><h2>Marketplace presets</h2><p>Presets are practical starting points, not guarantees of marketplace acceptance. Review each result and the marketplace's current requirements before publishing.</p></main>`,
  "/help": `<main class="prerender-shell"><h1>Help with background removal</h1><p>Upload people, products, pets, graphics, or other still PNG, JPEG, and WebP files by picker, folder drop, clipboard paste, or direct HTTPS image URL. HEIC, SVG, GIF, and animated WebP are not accepted.</p><h2>Choose the result you need</h2><p>Download a transparent PNG, refine edges with Keep, Remove, and Feather, or export with Amazon, Etsy, Shopify, and eBay presets. Apply one preset to a batch and download successful files as a ZIP.</p></main>`,
  "/contact": `<main class="prerender-shell"><h1>Contact KleanBG</h1><p>For product help, account support, privacy requests, abuse reports, or copyright concerns, email <a href="mailto:contact@kleanbg.com">contact@kleanbg.com</a>.</p></main>`,
  "/remove-bg-alternative": `<main class="prerender-shell"><h1>A practical remove.bg alternative</h1><p>KleanBG is a focused browser tool for finished product images. It needs no Canva account and sells no credit packs. Use transparent output, marketplace presets, simple edge refinement, small batch queues, and ZIP download.</p><h2>What is changing?</h2><p>remove.bg says browser background removal is moving into Canva. Its API transition to Leonardo.Ai is separate. KleanBG is not a drop-in API replacement.</p></main>`,
};

export function seoForPath(path: string): SeoConfig {
  return pageSeo[path] ?? fallbackSeo;
}

function escapeAttribute(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

export function renderSeoHead(config: SeoConfig): string {
  const image = config.image ?? defaultImage;
  const structuredData = config.structuredData ?? [];
  return [
    `<title>${escapeAttribute(config.title)}</title>`,
    `<meta name="description" content="${escapeAttribute(config.description)}" />`,
    `<meta name="robots" content="${escapeAttribute(config.robots ?? "index,follow")}" />`,
    `<link rel="canonical" href="${escapeAttribute(config.canonical)}" />`,
    `<meta property="og:title" content="${escapeAttribute(config.title)}" />`,
    `<meta property="og:description" content="${escapeAttribute(config.description)}" />`,
    `<meta property="og:type" content="${config.type ?? "website"}" />`,
    `<meta property="og:url" content="${escapeAttribute(config.canonical)}" />`,
    `<meta property="og:image" content="${escapeAttribute(image)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeAttribute(config.title)}" />`,
    `<meta name="twitter:description" content="${escapeAttribute(config.description)}" />`,
    `<meta name="twitter:image" content="${escapeAttribute(image)}" />`,
    `<script id="route-structured-data" type="application/ld+json">${JSON.stringify(structuredData).replaceAll("<", "\\u003c")}</script>`,
  ].join("\n    ");
}

export function rewriteSeoHtml(html: string, path: string): string {
  const withHead = html.replace(/<!-- route-seo:start -->[\s\S]*?<!-- route-seo:end -->/, `<!-- route-seo:start -->\n    ${renderSeoHead(seoForPath(path))}\n    <!-- route-seo:end -->`);
  const body = prerenderContent[path];
  return body ? withHead.replace('<div id="root"></div>', `<div id="root">${body}</div>`) : withHead;
}
